import { Component, EventEmitter, inject, Input, OnDestroy, OnInit, Output, OnChanges, SimpleChanges } from '@angular/core';
import { FormControl, FormGroup } from '@angular/forms';
import { Subject, takeUntil } from 'rxjs';
import { AddressDetailDTO, AddressDetailReadByMasterIdRequestDTO } from '../address-detail/address-detail.interface';
import { AddressDetailService } from '../address-detail/address-detail.service';
import { UserLoginService } from 'src/app/Common/UserLoginService/userlogin.service';

@Component({
  selector: 'app-uic-billing-delivery-address',
  templateUrl: './uic-billing-delivery-address.component.html',
  styleUrls: ['./uic-billing-delivery-address.component.css']
})
export class UicBillingDeliveryAddressComponent implements OnInit, OnChanges, OnDestroy {
  private destroy$ = new Subject<void>();
  
  @Input() masterId: number = 0;
  @Input() masterType: string = '';
  @Input() billingAddressId: number = 0;
  @Input() deliveryAddressId: number = 0;
  
  @Output() onAddressChange = new EventEmitter<{billing: number, delivery: number}>();
  @Output() onBillingAddressUpdate = new EventEmitter<AddressDetailDTO>();
  @Output() onDeliveryAddressUpdate = new EventEmitter<AddressDetailDTO>();
  
  addressDetailService = inject(AddressDetailService);
  userService = inject(UserLoginService);
  User = this.userService.User();
  
  availableAddresses: AddressDetailDTO[] = [];
  isLoading: boolean = false;
  sameAsBilling: boolean = false;
  showBillingAddressForm: boolean = false;
  showDeliveryAddressForm: boolean = false;
  
  billingAddressForm = new FormGroup({
    selectedAddressId: new FormControl(0)
  });
  
  deliveryAddressForm = new FormGroup({
    selectedAddressId: new FormControl(0)
  });

  ngOnInit(): void {
    this.initializeAddresses();
    this.setupFormListeners();
  }

  canAddAddress(): boolean {
    // Check if masterId is a valid number (not 0, null, undefined, or empty string)
    const validMasterId = this.masterId && Number(this.masterId) > 0;
    // Check if masterType is not empty
    const validMasterType = this.masterType && this.masterType.trim() !== '';
    
    return !!(validMasterId && validMasterType);
  }

  isBillingAddressSelected(): boolean {
    const billingAddressId = this.billingAddressForm.get('selectedAddressId')?.value || 0;
    return billingAddressId > 0;
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['masterId'] && !changes['masterId'].firstChange) {
      const newMasterId = changes['masterId'].currentValue;
      if (newMasterId && newMasterId > 0) {
        console.log('MasterId changed to:', newMasterId);
        this.loadAddresses();
      }
    }

    if (changes['billingAddressId'] && !changes['billingAddressId'].firstChange) {
      const newBillingId = changes['billingAddressId'].currentValue;
      if (newBillingId !== this.billingAddressForm.get('selectedAddressId')?.value) {
        this.billingAddressForm.patchValue({ selectedAddressId: newBillingId }, { emitEvent: false });
      }
    }

    if (changes['deliveryAddressId'] && !changes['deliveryAddressId'].firstChange) {
      const newDeliveryId = changes['deliveryAddressId'].currentValue;
      if (newDeliveryId !== this.deliveryAddressForm.get('selectedAddressId')?.value) {
        this.deliveryAddressForm.patchValue({ selectedAddressId: newDeliveryId }, { emitEvent: false });
        this.sameAsBilling = this.billingAddressId === newDeliveryId && this.billingAddressId > 0;
      }
    }
  }

  initializeAddresses(): void {
    if (this.masterId > 0) {
      this.loadAddresses();
    }
    
    if (this.billingAddressId > 0) {
      this.billingAddressForm.patchValue({ selectedAddressId: this.billingAddressId }, { emitEvent: false });
    }
    if (this.deliveryAddressId > 0) {
      this.deliveryAddressForm.patchValue({ selectedAddressId: this.deliveryAddressId }, { emitEvent: false });
      this.sameAsBilling = this.billingAddressId === this.deliveryAddressId && this.billingAddressId > 0;
    }
  }

  setupFormListeners(): void {
    this.billingAddressForm.get('selectedAddressId')?.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe(value => {
        console.log('Billing address changed:', value);
        this.emitAddressChanges();
        
        if (this.sameAsBilling) {
          this.deliveryAddressForm.patchValue({ selectedAddressId: value }, { emitEvent: false });
          this.emitAddressChanges();
        }
      });
    
    this.deliveryAddressForm.get('selectedAddressId')?.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe(value => {
        console.log('Delivery address changed:', value);
        if (!this.sameAsBilling) {
          this.emitAddressChanges();
        }
      });
  }

  emitAddressChanges(): void {
    const billingId = this.billingAddressForm.get('selectedAddressId')?.value || 0;
    const deliveryId = this.sameAsBilling ? billingId : (this.deliveryAddressForm.get('selectedAddressId')?.value || 0);
    
    console.log('Emitting address changes - Billing:', billingId, 'Delivery:', deliveryId);
    
    this.onAddressChange.emit({
      billing: billingId,
      delivery: deliveryId
    });
  }

  loadAddresses(): void {
    if (!this.masterId || this.masterId <= 0) {
      console.warn('Cannot load addresses: masterId is invalid', this.masterId);
      return;
    }

    console.log('Loading addresses for masterId:', this.masterId, 'masterType:', this.masterType);
    
    this.isLoading = true;
    const requestData: AddressDetailReadByMasterIdRequestDTO = {
      masterId: this.masterId,
      masterType: this.masterType,
      tenantId: this.User.companyId,
      tenantBranchId: this.User.loggedInBranchId
    };
    
    this.addressDetailService.AddressDetail_readByMasterId(requestData)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (res) => {
          console.log('Addresses loaded:', res.items);
          this.availableAddresses = res.items || [];
          this.isLoading = false;
        },
        error: (err) => {
          console.error('Error loading addresses:', err);
          this.availableAddresses = [];
          this.isLoading = false;
        }
      });
  }

  onSameAsBillingChange(event: any): void {
    this.sameAsBilling = event.target.checked;
    console.log('Same as billing changed:', this.sameAsBilling);
    
    if (this.sameAsBilling) {
      const billingAddressId = this.billingAddressForm.get('selectedAddressId')?.value;
      this.deliveryAddressForm.patchValue({ selectedAddressId: billingAddressId }, { emitEvent: false });
      this.emitAddressChanges();
    }
  }

  openAddAddressForm(type: 'billing' | 'delivery'): void {
    console.log('Opening add address form for:', this.masterId);
    console.log('Opening add address form for:', this.masterType);
    if (type === 'billing') {
      this.showBillingAddressForm = true;
    } else {
      this.showDeliveryAddressForm = true;
    }
  }

  closeAddAddressForm(type: 'billing' | 'delivery'): void {
    console.log('Closing add address form for:', this.masterId, this.masterType);
    if (type === 'billing') {
      this.showBillingAddressForm = false;
    } else {
      this.showDeliveryAddressForm = false;
    }
  }

  handleBillingAddressCreated(address: AddressDetailDTO): void {
    console.log('Billing address created:', address);
    
    // Set the newly created address as selected
    if (address && address.addressId) {
      this.billingAddressForm.patchValue({ selectedAddressId: address.addressId });
    }
    
    // Reload addresses and close form
    this.loadAddresses();
    this.closeAddAddressForm('billing');
  }

  handleDeliveryAddressCreated(address: AddressDetailDTO): void {
    console.log('Delivery address created:', address);
    
    // Set the newly created address as selected
    if (address && address.addressId) {
      this.deliveryAddressForm.patchValue({ selectedAddressId: address.addressId });
    }
    
    // Reload addresses and close form
    this.loadAddresses();
    this.closeAddAddressForm('delivery');
  }
  

  handleBillingAddressUpdate(updatedAddress: AddressDetailDTO): void {
    console.log('Billing address updated:', updatedAddress);
    this.loadAddresses();
    this.onBillingAddressUpdate.emit(updatedAddress);
  }

  handleDeliveryAddressUpdate(updatedAddress: AddressDetailDTO): void {
    console.log('Delivery address updated:', updatedAddress);
    this.loadAddresses();
    this.onDeliveryAddressUpdate.emit(updatedAddress);
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}